import { useState, useEffect } from 'react';
import * as api from '../../api/adminApi';
import { PageHeader, Pagination, StatusBadge, cardStyle, sectionTitleStyle, tableHeaderStyle, tableCellStyle, LoadingState, ErrorState, timeAgo } from './shared';

const PROVIDER_META: Record<string, { label: string; desc: string; color: string; logo: string; metrics: { label: string; key: string }[]; statusLabel?: string }> = {
  google_drive: {
    label: 'Google Drive', desc: 'Import & sync documents', color: '#4285F4',
    logo: '/IntPointsLogos/Google_Drive_icon_(2020).svg.png',
    metrics: [{ label: 'Connected users', key: 'connectedUsers' }, { label: 'Files imported', key: 'activeCount' }, { label: 'Sync errors (7d)', key: 'errorCount' }],
  },
  notion: {
    label: 'Notion', desc: 'Page import & export', color: '#1A1714',
    logo: '/IntPointsLogos/notion-icon.webp',
    metrics: [{ label: 'Connected users', key: 'connectedUsers' }, { label: 'Pages imported', key: 'activeCount' }, { label: 'Sync errors (7d)', key: 'errorCount' }],
  },
  slack: {
    label: 'Slack', desc: 'Notifications & sharing', color: '#E01E5A',
    logo: '/IntPointsLogos/Slack_icon_2019.svg.png',
    metrics: [{ label: 'Connected users', key: 'connectedUsers' }, { label: 'Messages sent', key: 'activeCount' }, { label: 'Webhook errors (7d)', key: 'errorCount' }],
  },
  gmail: {
    label: 'Gmail', desc: 'Email notifications', color: '#EA4335',
    logo: '/IntPointsLogos/Gmail_icon_(2020).svg.png',
    metrics: [{ label: 'Connected users', key: 'connectedUsers' }, { label: 'Emails processed', key: 'activeCount' }, { label: 'OAuth errors', key: 'errorCount' }],
    statusLabel: 'Beta',
  },
  linear: {
    label: 'Linear', desc: 'Issue tracking sync', color: '#5E6AD2',
    logo: '/IntPointsLogos/linear-icon.svg',
    metrics: [{ label: 'Connected users', key: 'connectedUsers' }, { label: 'Issues synced', key: 'activeCount' }, { label: 'Status', key: '_status' }],
    statusLabel: 'Coming soon',
  },
  dropbox: {
    label: 'Dropbox', desc: 'File storage sync', color: '#0061FF',
    logo: '/IntPointsLogos/dropbox-2-logo-png-transparent.png',
    metrics: [{ label: 'Connected users', key: 'connectedUsers' }, { label: 'Files synced', key: 'activeCount' }, { label: 'Sync errors (7d)', key: 'errorCount' }],
  },
};

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Awaited<ReturnType<typeof api.getIntegrationsSummary>> | null>(null);
  const [events, setEvents] = useState<Awaited<ReturnType<typeof api.getWebhookEvents>> | null>(null);
  const [eventPage, setEventPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [i, e] = await Promise.all([api.getIntegrationsSummary(), api.getWebhookEvents(eventPage)]);
      setIntegrations(i); setEvents(e);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [eventPage]);

  if (loading && !integrations) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const allProviders = ['google_drive', 'notion', 'slack', 'gmail', 'linear', 'dropbox'];
  const providerData = allProviders.map((p) => {
    const found = integrations?.find((i) => i.provider === p);
    return found ?? { provider: p, connectedUsers: 0, activeCount: 0, errorCount: 0, status: 'inactive' };
  });

  return (
    <div>
      <PageHeader title="Integrations" subtitle="Connected services across all users" />

      {/* Integration Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {providerData.map((int) => {
          const meta = PROVIDER_META[int.provider] ?? { label: int.provider, desc: '', color: '#8B7D6B', metrics: [] };
          const isInactive = int.connectedUsers === 0 && meta.statusLabel !== 'Coming soon';

          // Determine status badge
          let badge: React.ReactNode;
          if (int.errorCount > 0) {
            badge = <span style={{ fontSize: 13, fontWeight: 500, color: '#D44A3A' }}>{int.errorCount} errors</span>;
          } else if (meta.statusLabel === 'Coming soon') {
            badge = <span style={{ fontSize: 12, color: '#8B7D6B', border: '1px solid #E8E0D8', borderRadius: 4, padding: '2px 8px' }}>Coming soon</span>;
          } else if (meta.statusLabel === 'Beta') {
            badge = <span style={{ fontSize: 12, color: '#8B7D6B', border: '1px solid #E8E0D8', borderRadius: 4, padding: '2px 8px' }}>Beta</span>;
          } else if (int.connectedUsers > 0) {
            badge = <StatusBadge status="active" />;
          } else {
            badge = <span style={{ fontSize: 12, color: '#8B7D6B' }}>Inactive</span>;
          }

          return (
            <div key={int.provider} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: meta.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    <img src={meta.logo} alt={meta.label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1714' }}>{meta.label}</div>
                    <div style={{ fontSize: 12, color: '#8B7D6B' }}>{meta.desc}</div>
                  </div>
                </div>
                {badge}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {meta.metrics.map((m) => {
                  let val: string;
                  if (m.key === '_status') {
                    val = isInactive || meta.statusLabel === 'Coming soon' ? 'Not launched' : 'Active';
                  } else {
                    const raw = (int as Record<string, unknown>)[m.key];
                    val = meta.statusLabel === 'Coming soon' && m.key !== 'connectedUsers' ? '—' : String(raw ?? 0);
                  }
                  const isError = m.key === 'errorCount' && Number(val) > 0;
                  return (
                    <div key={m.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: '#8B7D6B' }}>{m.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: isError ? '#D44A3A' : m.key === 'errorCount' ? '#22883F' : '#1A1714' }}>
                        {meta.statusLabel === 'Coming soon' && m.key === 'connectedUsers' ? '—' : val}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Webhook Events */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={sectionTitleStyle}>Recent Webhook Events</span>
          <span style={{ fontSize: 13, color: '#D46A3A', fontWeight: 500, cursor: 'pointer' }}>View all</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Event Type</th>
              <th style={tableHeaderStyle}>Service</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Time</th>
            </tr>
          </thead>
          <tbody>
            {events?.events.map((e) => (
              <tr key={e.eventId}>
                <td style={{ ...tableCellStyle, fontFamily: '"JetBrains Mono", monospace', fontSize: 13 }}>{e.eventType}</td>
                <td style={tableCellStyle}>{e.eventType.includes('subscription') || e.eventType.includes('invoice') || e.eventType.includes('payment') ? 'Stripe' : e.eventType.includes('file') ? 'Google Drive' : e.eventType.includes('message') ? 'Slack' : '—'}</td>
                <td style={tableCellStyle}><StatusBadge status={e.processed ? 'processed' : e.error ? 'error' : 'pending'} /></td>
                <td style={{ ...tableCellStyle, color: '#8B7D6B' }}>{timeAgo(e.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {events && <Pagination page={events.page} totalPages={events.totalPages} total={events.total} label="events" onPageChange={setEventPage} />}
      </div>
    </div>
  );
}
